import { useState } from "react";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import api, { apiError } from "@/lib/api";

export default function AdminLogin({ onSuccess }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const { data } = await api.post("/api/auth/login", { email, password });
            // Store token in localStorage for subsequent API calls
            if (data.token) {
                localStorage.setItem("admin_token", data.token);
            }
            onSuccess(data);
        } catch (err) {
            setError(apiError(err, "Login failed. Check your email and password."));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-canvas px-5">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-32 right-[-10%] h-[420px] w-[420px] rounded-full opacity-[0.07]"
                style={{ background: "radial-gradient(circle, #7E35A8 0%, transparent 65%)" }}
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute bottom-[-15%] left-[-8%] h-[380px] w-[380px] rounded-full opacity-[0.07]"
                style={{ background: "radial-gradient(circle, #22B3A3 0%, transparent 65%)" }}
            />
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full max-w-sm rounded-3xl border border-slate-200/80 bg-white p-8 shadow-[0_30px_70px_-40px_rgba(22,50,79,0.35)]"
                data-testid="admin-login-card"
            >
                <img src="/assets/logo-full.png" alt="Work Island" className="h-12 w-auto" />
                <h1 className="mt-6 text-2xl font-bold tracking-tight text-ink">Admin Panel</h1>
                <p className="mt-1 text-sm font-medium text-ink/50">Sign in to manage leads, plans and gallery.</p>

                <form onSubmit={submit} className="mt-7 space-y-4">
                    <div>
                        <label htmlFor="admin-email" className="text-xs font-bold uppercase tracking-[0.14em] text-ink/50">
                            Email
                        </label>
                        <input
                            id="admin-email"
                            data-testid="admin-login-email"
                            type="email"
                            required
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-ink outline-none transition-colors focus:border-brand-blue/50"
                            placeholder="you@example.com"
                        />
                    </div>
                    <div>
                        <label htmlFor="admin-password" className="text-xs font-bold uppercase tracking-[0.14em] text-ink/50">
                            Password
                        </label>
                        <input
                            id="admin-password"
                            data-testid="admin-login-password"
                            type="password"
                            required
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-ink outline-none transition-colors focus:border-brand-blue/50"
                            placeholder="••••••••"
                        />
                    </div>

                    {error && (
                        <p data-testid="admin-login-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-600">
                            {error}
                        </p>
                    )}

                    <button
                        data-testid="admin-login-submit"
                        type="submit"
                        disabled={busy}
                        className="btn-gradient inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold text-white transition-opacity disabled:opacity-60"
                    >
                        <Lock size={15} strokeWidth={2.6} />
                        {busy ? "Signing in…" : "Sign In"}
                    </button>
                </form>
            </motion.div>
        </div>
    );
}
